import useRouter from 'use-react-router';
import { Dispatch } from 'redux';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { smsKeywordsTriggersSelector } from '@mm/state/selectors/triggerSelectors';
import {
  chatbotIsActiveOnConnectedPageSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { PageKind } from '@mm/state/model/PageModel';
import TriggerModel from '@mm/state/model/TriggerModel';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import modalActions from '@mm/state/modules/modalsState/actions';

export const useCreateSMSKeyword = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const {
    history,
    match: {
      params: { chatbotId },
    },
  } = useRouter<{ chatbotId: string }>();

  const { id: botIdAlternative } = useSelector(activeChatbotSelector);

  const botId = Number(chatbotId) || botIdAlternative;
  const triggerCRUDActions = useTriggerCRUDActions();
  const pageCURDActions = usePageCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();

  const { triggers, chatbotIsActive, chatbot } = useSelector(
    (state: StoreState) => ({
      triggers: smsKeywordsTriggersSelector(state),
      chatbotIsActive: chatbotIsActiveOnConnectedPageSelector(state),
      chatbot: activeChatbotSelector(state),
    }),
    shallowEqual,
  );
  const sortOrder =
    triggers.length > 0
      ? Math.max(...triggers.map((t: TriggerModel) => t.sortOrder)) + 1
      : 1;

  type CreateKeywordOptions = {
    withoutBotActivation?: boolean;
    initialPhrase?: string;
  };

  const createKeyword = (opts = {} as CreateKeywordOptions) => {
    const { withoutBotActivation = false, initialPhrase = 'KEYWORD' } = opts;

    if (withoutBotActivation || chatbotIsActive || chatbot.template) {
      return performKeywordCreation(initialPhrase);
    }

    dispatch(
      modalActions.openActivateInactiveBotModal({
        onBotActivateConfirm: () => performKeywordCreation(initialPhrase),
      }),
    );
  };

  const performKeywordCreation = async (initialPhrase: string) => {
    const dialogueResponse = await pageCURDActions.create({
      botId,
      name: 'Start',
      kind: PageKind.StartBot,
      chatStarterKind: 'sms_keywords',
    });

    const {
      page: { id: dialogueId },
    } = dialogueResponse;

    const trigger = await triggerCRUDActions.create(botId, {
      botId,
      sortOrder,
      randomize: false,
      phrases: [{ phrase: initialPhrase, sortOrder: 1 }],
      active: true,
      botResponses: [{ pageId: dialogueId, sortOrder: 1 }],
      kind: 'sms_keywords',
    });

    const { id: smsKeywordId } = trigger;

    widgetCRUDActions.create({
      pageId: dialogueId,
      sortOrder: 1,
      type: 'text',
      config: {
        body: 'Type STOP at any time to unsubscribe. Thanks for messaging us! We are excited to have you.',
      },
    });

    history.push(
      BotBuilderRoutes.smsKeywordEdit.getLink({
        chatbotId: botId,
        smsKeywordId,
        dialogueId,
      }),
    );
  };

  return { createKeyword };
};
